<template>
  <div class="cr-open-session">
    <div class="loading loading-lg" v-if="loading"></div>

    <div class="cr-box" v-else-if="settingsMissing.length > 0">
      <div class="empty">
        <div class="empty-icon">
          <fa-icon :icon="['fal', 'exclamation-triangle']" size="2x"/>
        </div>
        <p class="empty-title h6">Falta configurar: {{ settingsMissing.join(', ') }}</p>
        <p class="empty-subtitle">
          <router-link to="/cash-register/settings">Abrir configuração do caixa</router-link>
        </p>
      </div>
    </div>

    <div class="cr-opening" v-else-if="$can(OPEN_CASHIER)">
      <div class="cr-opening-head">
        <div class="cr-opening-icon">
          <fa-icon :icon="['fal', 'cash-register']"/>
        </div>
        <div>
          <div class="cr-opening-title">Abrir caixa</div>
          <div class="cr-opening-subtitle">
            {{ user.name }} · {{ todayLabel }} · o caixa fica no seu nome
          </div>
        </div>
      </div>

      <div class="cr-opening-question">Com que fundo de troco você começa?</div>

      <div class="cr-choices">
        <label class="cr-choice"
               :class="{active: opening.source.type === 'previous_session'}">
          <input type="radio" value="previous_session" v-model="opening.source.type"
                 @change="onSourceChange">
          <span class="cr-choice-check"><fa-icon :icon="['fal', 'check']"/></span>
          <span class="cr-choice-title">O que ficou na gaveta</span>
          <span class="cr-choice-value">{{ previousFund | currency }}</span>
          <span class="cr-choice-hint" v-if="lastClosed">
            do seu fechamento de {{ lastClosed.closedAt | date('datetime') }}
          </span>
          <span class="cr-choice-hint" v-else>
            Nenhum fechamento anterior. Você começa sem troco.
          </span>
        </label>
        <label class="cr-choice"
               :class="{active: opening.source.type === 'bank_account'}">
          <input type="radio" value="bank_account" v-model="opening.source.type"
                 @change="onSourceChange">
          <span class="cr-choice-check"><fa-icon :icon="['fal', 'check']"/></span>
          <span class="cr-choice-title">
            {{ previousFund > 0 ? 'Reforçar com uma conta' : 'Retirar de uma conta' }}
          </span>
          <span class="cr-choice-value">
            {{ opening.source.type === 'bank_account' ? openingFund : 0 | currency }}
          </span>
          <span class="cr-choice-hint" v-if="previousFund > 0">
            {{ previousFund | currency }} da gaveta mais o valor retirado do cofre,
            tesouraria ou banco.
          </span>
          <span class="cr-choice-hint" v-else>
            Cofre, tesouraria ou banco. A transferência entra no financeiro.
          </span>
        </label>
      </div>

      <div class="columns cr-opening-fields" v-if="opening.source.type === 'bank_account'">
        <div class="column col-5 col-sm-12 form-group"
             :class="{'has-error': $v.opening.source.bankAccountId.$error}">
          <label class="form-label">Conta de origem</label>
          <select class="form-select" v-model="opening.source.bankAccountId"
                  @blur="$v.opening.source.bankAccountId.$touch()">
            <option value="">[Selecione]</option>
            <option v-for="item in bankAccounts" :value="item.id" :key="item.id">
              {{ item.name }}
            </option>
          </select>
          <div class="form-input-hint" v-if="$v.opening.source.bankAccountId.$error">
            Campo obrigatório
          </div>
        </div>
        <div class="column col-3 col-sm-12 form-group"
             :class="{'has-error': $v.opening.fund.$error}">
          <label class="form-label">Valor retirado</label>
          <dx-input-number id="opening-fund"
                           v-model="opening.fund"
                           class="form-input text-right"
                           :precision="2"
                           maxlength="10"
                           @blur="$v.opening.fund.$touch()"/>
          <div class="form-input-hint" v-if="$v.opening.fund.$error">
            Informe um valor maior que zero
          </div>
        </div>
        <div class="column col-4 col-sm-12 form-group" v-if="previousFund > 0">
          <label class="form-label">Fundo total</label>
          <input type="text" class="form-input text-right text-bold" readonly
                 :value="openingFund | currency">
          <div class="form-input-hint text-gray">
            {{ previousFund | currency }} da gaveta + {{ withdrawnFund | currency }} retirados
          </div>
        </div>
      </div>

      <div class="cr-opening-notes">
        <a href="#" v-if="!opening.showNotes" @click.prevent="opening.showNotes = true">
          <fa-icon :icon="['fal', 'plus']" class="mr-1"/>Adicionar observação
        </a>
        <div class="form-group" v-else>
          <label class="form-label">Observação</label>
          <input type="text" class="form-input" v-model="opening.notes"
                 placeholder="Opcional. Fica registrada no caixa.">
        </div>
      </div>

      <div class="cr-opening-footer">
        <button class="btn btn-primary btn-lg btn-icon btn-icon-left"
                :class="{loading: opening.saving}"
                :disabled="opening.saving"
                @click="openSession">
          <fa-icon :icon="['fal', 'unlock']"/>Abrir caixa com {{ openingFund | currency }}
        </button>
      </div>
    </div>

    <div class="empty" v-else>
      <div class="empty-icon"><fa-icon :icon="['fal', 'lock']" size="3x"/></div>
      <p class="empty-title h5">Você não tem permissão para abrir caixa</p>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import { round } from 'lodash';
import { mapState } from 'vuex';
import { required, minValue } from 'vuelidate/lib/validators';
import { OPEN_CASHIER } from '@/data/actions/modules/financial';

export default {
  data() {
    return {
      OPEN_CASHIER,
      loading: true,
      settings: {},
      lastClosed: null,
      bankAccounts: [],
      opening: {
        saving: false,
        showNotes: false,
        fund: 0,
        notes: '',
        source: {
          type: 'previous_session',
          bankAccountId: '',
        },
      },
    };
  },
  computed: {
    ...mapState({
      user: state => state.auth.user,
    }),
    todayLabel() {
      return moment().format('dddd, DD/MM');
    },
    previousFund() {
      return this.lastClosed ? this.lastClosed.keptAsFund : 0;
    },
    withdrawnFund() {
      return Number(this.opening.fund) || 0;
    },
    openingFund() {
      return this.opening.source.type === 'bank_account'
        ? round(this.previousFund + this.withdrawnFund, 2)
        : this.previousFund;
    },
    settingsMissing() {
      const missing = [];
      if (!this.settings.bankAccount) missing.push('conta do dinheiro');
      if (!this.settings.revenueCategory) missing.push('categoria de receita');
      if (!this.settings.transferInCategory) missing.push('categoria de transferência (receita)');
      if (!this.settings.expenseCategory) missing.push('categoria de despesa');
      if (!this.settings.transferOutCategory) missing.push('categoria de transferência (despesa)');
      return missing;
    },
  },
  validations() {
    const fromAccount = this.opening.source.type === 'bank_account';
    return {
      opening: {
        fund: { required, minValue: minValue(fromAccount ? 0.01 : 0) },
        source: {
          bankAccountId: fromAccount ? { required } : {},
        },
      },
    };
  },
  async mounted() {
    try {
      const [{ data: settings }, { data: session }] = await Promise.all([
        this.$http.get('/cash-register/settings'),
        this.$http.get('/cash-register/session'),
      ]);
      this.settings = settings;
      if (session.status === 'open') {
        this.$emit('opened', session);
        return;
      }
      this.lastClosed = session.lastClosed || null;
      this.opening.fund = this.lastClosed ? this.lastClosed.keptAsFund : 0;
      await this.loadBankAccounts();
    } catch (e) {
      this.$toast.error(e);
    }
    this.loading = false;
  },
  methods: {
    loadBankAccounts() {
      const params = { active: true, limit: 0 };
      return this.$http.get('/bank-accounts', { params })
        .then(({ data }) => {
          const cashId = this.settings.bankAccount ? this.settings.bankAccount.id : null;
          this.bankAccounts = data.items.filter(({ id }) => id !== cashId);
        })
        .catch(() => {});
    },
    onSourceChange() {
      if (this.opening.source.type === 'previous_session') {
        this.opening.fund = this.lastClosed ? this.lastClosed.keptAsFund : 0;
      } else {
        this.opening.fund = 0;
      }
      this.$v.opening.$reset();
    },
    openSession() {
      this.$v.opening.$touch();
      if (this.$v.opening.$error) return;

      this.opening.saving = true;
      const data = {
        fund: this.openingFund,
        source: {
          type: this.opening.source.type,
          bankAccountId: this.opening.source.type === 'bank_account'
            ? this.opening.source.bankAccountId
            : null,
        },
        notes: this.opening.notes || null,
      };
      this.$http.post('/cash-register/session/open', data)
        .then(({ data: session }) => {
          this.$toast.success('Caixa aberto.');
          this.$root.$emit('cash-register:session', session);
          this.$emit('opened', session);
        })
        .catch((e) => {
          this.$toast.error(e);
        })
        .finally(() => {
          this.opening.saving = false;
        });
    },
  },
};
</script>

<style lang="scss">
@import "./src/assets/scss/_variables.scss";
.cr-open-session {
  .cr-opening {
    max-width: 46rem;
  }
  .cr-opening-head {
    align-items: center;
    display: flex;
    gap: $layout-spacing;
    margin-bottom: $layout-spacing-lg;
    .cr-opening-icon {
      align-items: center;
      background-color: lighten($primary-color, 42%);
      border-radius: 50%;
      color: $primary-color;
      display: flex;
      font-size: 1.2rem;
      height: 2.6rem;
      justify-content: center;
      width: 2.6rem;
    }
    .cr-opening-title {
      font-size: $font-size-lg;
      font-weight: 600;
    }
    .cr-opening-subtitle {
      color: $gray-color-dark;
      font-size: $font-size-sm;
    }
  }
  .cr-opening-question {
    font-weight: 600;
    margin-bottom: $layout-spacing-sm;
  }
  .cr-choices {
    display: grid;
    gap: $layout-spacing;
    grid-template-columns: repeat(auto-fit, minmax(14rem, 1fr));
    margin-bottom: $layout-spacing;
  }
  .cr-choice {
    background-color: $light-color;
    border: $border-color solid $border-width;
    border-radius: $border-radius;
    cursor: pointer;
    display: block;
    margin: 0;
    padding: $layout-spacing $layout-spacing $layout-spacing 2.4rem;
    position: relative;
    input {
      opacity: 0;
      position: absolute;
    }
    .cr-choice-check {
      align-items: center;
      border: $border-color-dark solid $border-width;
      border-radius: 50%;
      color: transparent;
      display: flex;
      font-size: .55rem;
      height: 1rem;
      justify-content: center;
      left: $layout-spacing;
      position: absolute;
      top: $layout-spacing + .1rem;
      width: 1rem;
    }
    .cr-choice-title {
      display: block;
      font-weight: 600;
    }
    .cr-choice-value {
      color: $dark-color;
      display: block;
      font-size: 1.1rem;
      font-weight: 700;
      margin: .1rem 0;
    }
    .cr-choice-hint {
      color: $gray-color-dark;
      display: block;
      font-size: $font-size-xs;
    }
    &:hover {
      border-color: $primary-color;
    }
    &.active {
      background-color: lighten($primary-color, 42%);
      border-color: $primary-color;
      .cr-choice-check {
        background-color: $primary-color;
        border-color: $primary-color;
        color: $light-color;
      }
      .cr-choice-value {
        color: $primary-color;
      }
    }
  }
  .cr-opening-fields {
    background-color: $gray-color-ultra-light;
    border-radius: $border-radius;
    margin: 0 0 $layout-spacing 0;
    padding: $layout-spacing-sm $layout-spacing 0;
  }
  .cr-opening-notes {
    font-size: $font-size-sm;
    margin-bottom: $layout-spacing;
  }
  .cr-opening-footer {
    border-top: $border-color solid $border-width;
    padding-top: $layout-spacing;
  }
}
</style>
